<template>
  <v-dialog
    v-model="active"
    width="1000px"
  >
    <template v-slot:activator="{ on }">
      <slot name="activator" v-bind="{ on }"></slot>
    </template>
    <v-card>
      <v-card-title
        class="title grey lighten-4"
        primary-title
      >
        Anexos
      </v-card-title>
      <v-skeleton-loader
        v-if="!setupFinished"
        :loading="!setupFinished"
        type="article"
        class="mx-auto flex-fill"
        width="100%"
      />
      <v-fade-transition>
        <v-container
          v-if="setupFinished"
          class="fill-height"
        >
          <!-- Images -->
          <v-row
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              @dragover.prevent.stop="onDragOver"
              outlined
              width="100%"
              @click="onFileInputClick"
              @dragenter.passive.stop="onDragEnter"
              @dragexit.stop="onDragExit"
              @dragleave.stop="onDragLeave"
              @drop="onDrop"
              class="dropzone"
            >
              <v-card-text>
                <v-row
                  justify="center"
                  align="center"
                  class="fill-height ma-0"
                >
                  <input
                    type="file"
                    ref="file-input"
                    @change="onFileInputChange"
                    class="file-input"
                    multiple
                  >
                  <empty-state
                    :color="isDragging ? 'accent' : ''"
                  >
                    <template v-slot:icon>
                      <v-icon :color="isDragging ? 'accent' : ''">mdi-cloud-upload</v-icon>
                    </template>
                    <template v-slot:description>
                      Arraste ou clique para adicionar arquivos
                    </template>
                    <template v-slot:overlay>
                      <v-overlay
                        :value="isDragging"
                        absolute
                        opacity=".8"
                      >
                        <span class="subtitle-1 font-weight-bold white--text">Solte os arquivos para adicioná-los ao upload</span>
                      </v-overlay>
                    </template>
                  </empty-state>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>
          <v-row
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              width="100%"
              elevation="10"
            >
              <v-row
                align="center"
                justify="space-between"
                class="ma-0"
              >
                <v-card-title class="title">Upload de anexos</v-card-title>
                <transition name="error">
                  <div class="light-blue--text" v-if="fileUploader.isFinishingStream">
                    Estamos finalizando o upload, por favor aguarde.
                  </div>
                  <div class="error--text" v-if="fileUploader.deleting">
                    Estamos deletando seu arquivo, por favor aguarde.
                  </div>
                </transition>
                <div class="body-1 ma-4">
                  <b>
                    Arquivos Upados {{ fileUploader.uploadedQueue.length }}/{{ fileUploader.fullFilesList.length}}
                  </b>
                </div>
              </v-row>
              <v-divider></v-divider>
              <v-card-text>
                <v-row
                  justify="space-between"
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    cols="11"
                  >
                    <v-progress-linear
                      :indeterminate="fileUploader.isFinishingStream"
                      :query="fileUploader.isFinishingStream"
                      :value="fileUploader.progress"
                      rounded
                    />
                  </v-col>
                  <v-col
                    cols="1"
                  >
                    <div>{{fileUploader.progress}}%</div>
                  </v-col>
                </v-row>
                <v-row
                  v-if="fileUploader.loading"
                  justify="center"
                  align="center"
                  class="ma-0 my-10"
                >
                  <v-progress-circular
                    indeterminate
                    color="accent"
                  />
                  <div class="caption ml-4">Carregando arquivos...</div>
                </v-row>
                <v-row
                  v-else
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    v-for="file in currFilesPage"
                    :key="file.name"
                    cols="6"
                    xs="3"
                    sm="3"
                    md="4"
                    lg="4"
                    xl="4"
                  >
                    <v-card
                      hover
                      class="pa-2 file-card"
                    >
                      <v-row
                        justify="space-between"
                        align="center"
                        class="ma-0 flex-nowrap"
                      >
                        <v-icon class="px-1" color="red">mdi-attachment</v-icon>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="body-2 px-1 text-break text-truncate">{{file.name}}</span>
                          </template>
                          <span>{{file.name}}</span>
                        </v-tooltip>
                        <transition name="fade" mode="out-in">
                          <v-icon
                            v-if="checkFileUploadFinished(file)"
                            color="success"
                            class="px-1"
                          >
                            mdi-check-circle
                          </v-icon>
                          <v-progress-circular
                            v-else
                            :indeterminate="file.uploadStarted && file.uploadProgress === 0"
                            :value="file | getCurrFileProgress"
                            color="primary"
                            class="px-1"
                            width="3"
                          >
                            <span class="upload-progress__text">{{file | getCurrFileProgress}}</span>
                          </v-progress-circular>
                        </transition>
                      </v-row>
                      <v-btn
                        v-if="!fileUploader.uploading"
                        @click="() => {fileUploader.remove(file)}"
                        small
                        icon
                        :loading="fileUploader.deleting"
                        absolute
                        color="error"
                        elevation="0"
                        class="delete-file-btn"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row
                  v-if="maxPage > 1"
                  align="center"
                  class="ma-0 my-2"
                >
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="currPageIdx <= 0"
                        @click="() => {setPage(0)}"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-page-first</v-icon>
                      </v-btn>
                    </template>
                    <span>Primeira página</span>
                  </v-tooltip>
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="currPageIdx <= 0"
                        @click="lastPage"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                    </template>
                    <span>Página anterior</span>
                  </v-tooltip>
                  <v-spacer/>
                  <span class="subtitle-2">Página: {{currPageIdx + 1}}/{{maxPage}}</span>
                  <v-spacer/>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :disabled="nextPageIsDisabled"
                        @click="nextPage"
                        @on="on"
                        class="mx-2"
                        icon
                      >
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </template>
                    <span>Próxima página</span>
                  </v-tooltip>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="nextPageIsDisabled"
                        @click="() => {setPage(maxPage - 1)}"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-page-last</v-icon>
                      </v-btn>
                    </template>
                    <span>Última página</span>
                  </v-tooltip>
                </v-row>
                <v-row
                  align="center"
                  class="ma-0"
                >
                  <v-btn
                    color="warning"
                    outlined
                    block
                    @click="() => { fileUploader.start() }"
                    :disabled="fileUploader.uploading || !fileUploader.toUploadQueue.length"
                  >
                    Começar Upload
                  </v-btn>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>
        </v-container>
      </v-fade-transition>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          color="primary"
          text
          @click="active = false"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import EmptyState from "../Helpers/EmptyState"
  import {cloneDeep, gte, isEmpty, isNull, isUndefined} from "lodash/lang"
  import {FILES_PER_PAGE} from "../../helpers/upload/constants"
  import {UPLOADER_URL} from "../../globals"
  import {FileUploader} from "../../helpers/flight-provider/mixins"
  import {listFlightOrderAttachments} from "../../api/api-flight-provider"
  import UploadFileHolder from "../../helpers/upload/file-holder"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../helpers/notify"

  export default {
    name: "FlightOrderAttachmentsDialog",
    components: {EmptyState},
    props: {
      idOrder: {
        type: Number,
        required: true,
      },
    },
    data: () => ({
      active: false,

      setupFinished: false,

      fileUploader: null,

      isDragging: false,

      // Pagination
      currPageIdx: 0,
      currPageFiles: [],
    }),

    methods: {
      onFileInputClick() {
        if (!this.fileUploader.canUpload) return
        this.$refs["file-input"].click()
      },
      onFileInputChange(event) {
        const files = Array.from(event.target.files)
        if (isEmpty(files) || this.fileUploader.loading) return
        this.fileUploader?.add(files)
      },

      onDrop(event) {
        event.preventDefault()

        if (!this.fileUploader?.canUpload) return

        this.isDragging = false

        if (!event.dataTransfer.items) return

        const files = this.getFilesFromDataTransfer(event.dataTransfer)
        if (isEmpty(files) || this.fileUploader.loading) return
        this.fileUploader?.add(files)
      },
      onDragEnter() {
        if (!this.fileUploader?.canUpload) return
        this.isDragging = true
      },
      onDragExit() {
        if (!this.isFirefox && !this.isOtherBrowser) return

        if (!this.fileUploader?.canUpload) return
        this.isDragging = false
      },
      onDragOver(event) {
        event.preventDefault()
      },
      onDragLeave() {
        if (!this.fileUploader?.canUpload) return
        this.isDragging = false
      },

      nextPage() {
        if (this.currPageIdx >= this.maxPage - 1) return
        this.currPageIdx++
      },
      lastPage() {
        if (this.currPageIdx - 1 < 0) return
        this.currPageIdx--
      },
      setPage(idx) {
        if (isUndefined(idx) || isNull(idx) || idx >= this.maxPage || idx < 0) return
        this.currPageIdx = idx
      },

      getFilesFromDataTransfer(dataTransfer) {
        const addedFiles = []
        for (const item of dataTransfer.items) {
          if (item.kind !== "file") {
            continue
          }

          addedFiles.push(item.getAsFile())
        }

        return addedFiles
      },

      checkFileUploadFinished(file) {
        return file.uploadCompleted && gte(file.uploadProgress, 100)
      },
      onMounted() {
        this.fileUploader = new FileUploader(
          `${UPLOADER_URL}/upload/attachments/flight-order/${this.idOrder}`, this.idOrder, this.setPage
        )

        this.fileUploader.loading = true

        const authToken = this.$apiAuth.getAuthToken()
        listFlightOrderAttachments(authToken, this.idOrder)
          .then((res) => {
            const loadedFiles = res.data.data
            this.fileUploader.uploadedQueue = loadedFiles.map((obj) => {
              const uploaderFileHolder = new UploadFileHolder({name: obj.name})
              uploaderFileHolder.uploadProgress = 100
              uploaderFileHolder.uploadCompleted = true
              return uploaderFileHolder
            })

            if (this.fileUploader.uploadedQueue.length > 0) this.fileUploader.progress = 100
            this.fileUploader.loading = false
          })
          .catch(() => {
            this.$notify(new NotificationOptions({
              message: "Erro ao carregar arquivos upados, atualize a página.",
              type: NOTIFICATION_TYPES.WARNING,
            }))
            this.fileUploader.loading = false
          })

        this.setupFinished = true
      },
    },
    filters: {
      getCurrFileProgress(file) {
        return file.uploadProgress ? Math.trunc(file.getUploadProgress()) : 0
      },
    },
    computed: {
      // Helpers
      isFirefox() {
        return typeof InstallTrigger !== "undefined"
      },
      isChrome() {
        return !!window.chrome && (!!window.chrome["webstore"] || !!window.chrome["runtime"])
      },
      isOtherBrowser() {
        return !this.isFirefox && !this.isChrome
      },

      // Image Pages
      maxPage() {
        return Math.ceil(this.fileUploader?.fullFilesList.length / FILES_PER_PAGE)
      },
      currFilesPage() {
        const initIndex = this.currPageIdx * FILES_PER_PAGE
        const fullFilesListClone = cloneDeep(this.fileUploader?.fullFilesList)
        return fullFilesListClone.slice(initIndex, initIndex + FILES_PER_PAGE)
      },
      nextPageIsDisabled() {
        return gte(this.currPageIdx, (this.maxPage - 1))
      },
    },
    mounted() {
      this.onMounted()
    }
  }
</script>

<style scoped lang="scss">
  .file-input {
    display: none;
  }

  .file-card {
    .delete-file-btn {
      top: 0 !important;
      right: 0 !important;
      transform: translate(50%, -50%) scale(0) !important;
      transition: transform 250ms cubic-bezier(0.19, 1, 0.22, 1) !important;
    }

    &:hover {
      .delete-file-btn {
        transform: translate(50%, -50%) scale(1) !important;
      }
    }
  }

  .dropzone {
    * {
      pointer-events: none !important;
    }
  }
</style>
